// See app/serializers/rest/notification_group_serializer.rb

import type { AccountWarningAction } from 'mastodon/models/notification_group';

import type { ApiAccountJSON } from './accounts';
import type { ApiGameJSON } from './games';
import type { ApiReportJSON } from './reports';
import type { ApiStatusJSON } from './statuses';

// See app/model/notification.rb
export const allNotificationTypes: NotificationType[] = [
  'follow',
  'follow_request',
  'favourite',
  'reblog',
  'mention',
  'quote',
  'poll',
  'status',
  'update',
  'admin.sign_up',
  'admin.report',
  'moderation_warning',
  'severed_relationships',
  'annual_report',
  'game_challenge',
  'community_join_request',
  'community_membership_approved',
  'community_invite',
  'community_removed',
  'community_post',
  'community_room_started',
  'room_started',
  'room_invite',
  'org_invite',
  'org_creator_pending',
  'premium_expiring',
  'premium_expiring_soon',
];

export type NotificationWithStatusType =
  | 'favourite'
  | 'reblog'
  | 'status'
  | 'mention'
  | 'quote'
  | 'poll'
  | 'update'
  | 'quoted_update'
  | 'community_post'
  | 'community_room_started'
  | 'room_started';

export type NotificationType =
  | NotificationWithStatusType
  | 'room_invite'
  | 'follow'
  | 'follow_request'
  | 'moderation_warning'
  | 'severed_relationships'
  | 'admin.sign_up'
  | 'admin.report'
  | 'annual_report'
  | 'game_challenge'
  | 'community_join_request'
  | 'community_membership_approved'
  | 'community_invite'
  | 'community_removed'
  | 'org_invite'
  | 'org_creator_pending'
  | 'premium_expiring'
  | 'premium_expiring_soon';

export interface ApiCommunityJoinRequestNotificationPayloadJSON {
  id: string;
  message: string | null;
  created_at: string;
  community: {
    id: string;
    slug: string;
    name: string;
  };
}

export interface ApiCommunityRefJSON {
  id: string;
  slug: string;
  name: string;
}

export interface BaseNotificationJSON {
  id: string;
  type: NotificationType;
  created_at: string;
  group_key: string;
  account: ApiAccountJSON;
}

export interface BaseNotificationGroupJSON {
  group_key: string;
  notifications_count: number;
  type: NotificationType;
  sample_account_ids: string[];
  latest_page_notification_at: string; // FIXME: This will only be present if the notification group is returned in a paginated list, not requested directly
  most_recent_notification_id: string;
  page_min_id?: string;
  page_max_id?: string;
}

// `room` and `community` are sent for one case only: a community_room_started
// notification whose room has no announcement post (every room in a private
// community). It then has no status, and carries the room the way a room
// invite does. Optional, so an older server's payload still type-checks.
interface NotificationGroupWithStatusJSON extends BaseNotificationGroupJSON {
  type: NotificationWithStatusType;
  status_id: string | null;
  room?: ApiRoomInviteRoomJSON | null;
  community?: ApiCommunityRefJSON | null;
}

interface NotificationWithStatusJSON extends BaseNotificationJSON {
  type: NotificationWithStatusType;
  status: ApiStatusJSON | null;
  room?: ApiRoomInviteRoomJSON | null;
  community?: ApiCommunityRefJSON | null;
}

interface ReportNotificationGroupJSON extends BaseNotificationGroupJSON {
  type: 'admin.report';
  report: ApiReportJSON;
}

interface ReportNotificationJSON extends BaseNotificationJSON {
  type: 'admin.report';
  report: ApiReportJSON;
}

type SimpleNotificationTypes = 'follow' | 'follow_request' | 'admin.sign_up';
interface SimpleNotificationGroupJSON extends BaseNotificationGroupJSON {
  type: SimpleNotificationTypes;
}

interface SimpleNotificationJSON extends BaseNotificationJSON {
  type: SimpleNotificationTypes;
}

export interface ApiAccountWarningJSON {
  id: string;
  action: AccountWarningAction;
  text: string;
  status_ids: string[];
  created_at: string;
  target_account: ApiAccountJSON;
  appeal: unknown;
}

interface ModerationWarningNotificationGroupJSON extends BaseNotificationGroupJSON {
  type: 'moderation_warning';
  moderation_warning: ApiAccountWarningJSON;
}

interface ModerationWarningNotificationJSON extends BaseNotificationJSON {
  type: 'moderation_warning';
  moderation_warning: ApiAccountWarningJSON;
}

export interface ApiAccountRelationshipSeveranceEventJSON {
  id: string;
  type: 'account_suspension' | 'domain_block' | 'user_domain_block';
  purged: boolean;
  target_name: string;
  followers_count: number;
  following_count: number;
  created_at: string;
}

interface AccountRelationshipSeveranceNotificationGroupJSON extends BaseNotificationGroupJSON {
  type: 'severed_relationships';
  event: ApiAccountRelationshipSeveranceEventJSON;
}

interface AccountRelationshipSeveranceNotificationJSON extends BaseNotificationJSON {
  type: 'severed_relationships';
  event: ApiAccountRelationshipSeveranceEventJSON;
}

export interface ApiAnnualReportEventJSON {
  year: string;
}

interface AnnualReportNotificationGroupJSON extends BaseNotificationGroupJSON {
  type: 'annual_report';
  annual_report: ApiAnnualReportEventJSON;
}

interface GameChallengeNotificationJSON extends BaseNotificationJSON {
  type: 'game_challenge';
  game: ApiGameJSON;
}

interface GameChallengeNotificationGroupJSON extends BaseNotificationGroupJSON {
  type: 'game_challenge';
  game: ApiGameJSON;
}

interface CommunityJoinRequestNotificationJSON extends BaseNotificationJSON {
  type: 'community_join_request';
  community_join_request: ApiCommunityJoinRequestNotificationPayloadJSON;
}

interface CommunityJoinRequestNotificationGroupJSON extends BaseNotificationGroupJSON {
  type: 'community_join_request';
  community_join_request: ApiCommunityJoinRequestNotificationPayloadJSON;
}

interface CommunityMembershipApprovedNotificationJSON extends BaseNotificationJSON {
  type: 'community_membership_approved';
  community: ApiCommunityRefJSON;
}

interface CommunityMembershipApprovedNotificationGroupJSON extends BaseNotificationGroupJSON {
  type: 'community_membership_approved';
  community: ApiCommunityRefJSON;
}

interface CommunityInviteNotificationJSON extends BaseNotificationJSON {
  type: 'community_invite';
  community: ApiCommunityRefJSON;
}

interface CommunityInviteNotificationGroupJSON extends BaseNotificationGroupJSON {
  type: 'community_invite';
  community: ApiCommunityRefJSON;
}

// Removal from a community. Carries no actor: the copy deliberately does not
// say who removed the account, only that it happened.
interface CommunityRemovedNotificationJSON extends BaseNotificationJSON {
  type: 'community_removed';
  community: ApiCommunityRefJSON;
}

interface CommunityRemovedNotificationGroupJSON extends BaseNotificationGroupJSON {
  type: 'community_removed';
  community: ApiCommunityRefJSON;
}

// A Premium pass about to run out. Only the expiry travels: the client shows
// WHEN Premium ends, and nothing else about a payment record belongs in a
// notification payload. Optional throughout for forward compatibility -- a
// client that gets no pass still renders the warning, just without the date.
export interface ApiPremiumPassJSON {
  id: string;
  expires_at: string;
}

interface PremiumExpiringNotificationJSON extends BaseNotificationJSON {
  type: 'premium_expiring' | 'premium_expiring_soon';
  premium_pass?: ApiPremiumPassJSON;
}

interface PremiumExpiringNotificationGroupJSON extends BaseNotificationGroupJSON {
  type: 'premium_expiring' | 'premium_expiring_soon';
  premium_pass?: ApiPremiumPassJSON;
}

// A room invite carries the room itself rather than a status: a private room
// (or one in a private community) has no announcement post to point at, and
// pointing at one was what made invites impossible there. Optional throughout
// for forward compatibility with servers that predate the change — a client
// that gets no room falls back to the plain "X invited you" line.
export interface ApiRoomInviteRoomJSON {
  id: string;
  title: string;
  kind?: 'audio' | 'video';
  state: 'live' | 'ended';
  visibility?: 'public' | 'private';
  participant_count: number;
  host_account: ApiAccountJSON;
}

interface RoomInviteNotificationJSON extends BaseNotificationJSON {
  type: 'room_invite';
  room?: ApiRoomInviteRoomJSON | null;
}

interface RoomInviteNotificationGroupJSON extends BaseNotificationGroupJSON {
  type: 'room_invite';
  room?: ApiRoomInviteRoomJSON | null;
}

export interface ApiOrganizationRefJSON {
  id: string;
  slug: string;
  name: string;
}

interface OrgInviteNotificationJSON extends BaseNotificationJSON {
  type: 'org_invite';
  organization: ApiOrganizationRefJSON;
}
interface OrgInviteNotificationGroupJSON extends BaseNotificationGroupJSON {
  type: 'org_invite';
  organization: ApiOrganizationRefJSON;
}
interface OrgCreatorPendingNotificationJSON extends BaseNotificationJSON {
  type: 'org_creator_pending';
  organization: ApiOrganizationRefJSON;
}
interface OrgCreatorPendingNotificationGroupJSON extends BaseNotificationGroupJSON {
  type: 'org_creator_pending';
  organization: ApiOrganizationRefJSON;
}

export type ApiNotificationJSON =
  | SimpleNotificationJSON
  | ReportNotificationJSON
  | AccountRelationshipSeveranceNotificationJSON
  | NotificationWithStatusJSON
  | ModerationWarningNotificationJSON
  | GameChallengeNotificationJSON
  | CommunityJoinRequestNotificationJSON
  | CommunityMembershipApprovedNotificationJSON
  | CommunityInviteNotificationJSON
  | CommunityRemovedNotificationJSON
  | RoomInviteNotificationJSON
  | OrgInviteNotificationJSON
  | OrgCreatorPendingNotificationJSON
  | PremiumExpiringNotificationJSON;

export type ApiNotificationGroupJSON =
  | SimpleNotificationGroupJSON
  | ReportNotificationGroupJSON
  | AccountRelationshipSeveranceNotificationGroupJSON
  | NotificationGroupWithStatusJSON
  | ModerationWarningNotificationGroupJSON
  | AnnualReportNotificationGroupJSON
  | GameChallengeNotificationGroupJSON
  | CommunityJoinRequestNotificationGroupJSON
  | CommunityMembershipApprovedNotificationGroupJSON
  | CommunityInviteNotificationGroupJSON
  | CommunityRemovedNotificationGroupJSON
  | RoomInviteNotificationGroupJSON
  | OrgInviteNotificationGroupJSON
  | OrgCreatorPendingNotificationGroupJSON
  | PremiumExpiringNotificationGroupJSON;

export interface ApiNotificationGroupsResultJSON {
  accounts: ApiAccountJSON[];
  statuses: ApiStatusJSON[];
  notification_groups: ApiNotificationGroupJSON[];
}

export interface ApiNotificationRequestJSON {
  id: string;
  created_at: string;
  updated_at: string;
  notifications_count: string;
  account: ApiAccountJSON;
  last_status?: ApiStatusJSON;
}
